import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { globalDefinitions } from '@/defined-translations'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { ErrorLayout } from '@/modules/shared/components/error-layout/ErrorLayout'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { NotFoundError } from '@/modules/shared/components/not-found-error/NotFoundError'
import { isProduction } from '@/utils/environment'
import { AwellHostedPage } from '../../components/awell-hosted-page/AwellHostedPage'
import { useQuestionnaireCareplanActivity } from '../../hooks/useQuestionnaireCareplanActivity'
import { QuestionnaireEngineProvider } from '../../providers/QuestionnaireEngineProvider'
import { QuestionnaireFlowPage } from '../questionnaire-flow/QuestionnaireFlow.page'

export function IntramurosQuestionnairePage() {
  useHideNativeNavigation()

  const navigate = useNavigate()
  const { slug } = useParams()

  const {
    data: careplanActivity,
    isLoading: isCareplanActivityLoading,
    error: careplanActivityError,
  } = useQuestionnaireCareplanActivity(slug)

  // Template variables
  // ------------------------------------------------------------------------- /

  const isAwell = useMemo(() => {
    return careplanActivity?.questionnaire?.driver === 'awell'
  }, [careplanActivity])

  const errorMessage = useMemo(() => {
    const defaultMessage = t({
      id: 'questionnaireActivity.errorMessage',
      message: 'Er is iets misgegaan bij het laden van de vraaglijst',
    })
    if (careplanActivityError && !isProduction()) {
      return defaultMessage + ': ' + careplanActivityError.message
    }

    return defaultMessage
  }, [careplanActivityError])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleClick() {
    navigate(-1)
  }

  if (isAwell && slug) {
    return <AwellHostedPage activityId={Number(slug)} />
  }

  if (isCareplanActivityLoading) {
    return <AnimatedLoader />
  }

  if (!slug || !careplanActivity) {
    return <NotFoundError onClick={handleClick} />
  }

  if (careplanActivityError || !careplanActivity.questionnaire) {
    return (
      <ErrorLayout
        title={t({
          id: 'questionnaireActivity.errorTitle',
          message: 'Er is iets misgegaan bij het laden van de vraaglijst',
        })}
        message={errorMessage}
        cta={{
          label: i18n._(globalDefinitions.goBack),
          onClick: handleClick,
        }}
      />
    )
  }

  return (
    <QuestionnaireEngineProvider activity={careplanActivity}>
      <QuestionnaireFlowPage />
    </QuestionnaireEngineProvider>
  )
}
